import React, { useCallback, useEffect, useState } from "react";
import api, { errMsg } from "../api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { X, Upload, Trash2, Users, FileText, Loader2, CheckCircle2 } from "lucide-react";
import DropZone from "./DropZone";

/**
 * Letterhead (Firmenbriefpapier) manager.
 * Admin/Master can: upload PNG/JPG/PDF, rename, set per-template whitelist,
 * delete, and globally enable users via `can_use_letterhead`.
 */
export default function LetterheadManager({ open, onClose, onChanged, lang = "de" }) {
    const [items, setItems] = useState([]);
    const [users, setUsers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null); // letterhead being edited
    const [uploadName, setUploadName] = useState("");

    const load = useCallback(async () => {
        try {
            const [a, b] = await Promise.all([
                api.get("/letterheads"),
                api.get("/users"),
            ]);
            setItems(a.data || []);
            setUsers((b.data || []).filter((u) => u.role !== "deactivated"));
        } catch (e) {
            toast.error(errMsg(e, "Fehler beim Laden"));
        }
    }, []);
    useEffect(() => { if (open) load(); }, [open, load]);

    const doUpload = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("name", (uploadName || file.name.replace(/\.[^.]+$/, "")).slice(0, 120));
            await api.post("/letterheads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(lang === "de" ? "Briefpapier hochgeladen" : "Letterhead uploaded");
            setUploadName("");
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(errMsg(e, "Upload fehlgeschlagen"));
        } finally { setBusy(false); }
    };

    const delLetterhead = async (id) => {
        if (!window.confirm(lang === "de" ? "Briefpapier löschen?" : "Delete letterhead?")) return;
        try { await api.delete(`/letterheads/${id}`); toast.success("OK"); await load(); onChanged?.(); }
        catch (e) { toast.error(errMsg(e)); }
    };

    const saveEditing = async () => {
        if (!editing) return;
        try {
            await api.put(`/letterheads/${editing.id}`, {
                name: editing.name,
                allowed_user_ids: editing.allowed_user_ids || [],
            });
            toast.success(lang === "de" ? "Gespeichert" : "Saved");
            setEditing(null);
            await load();
            onChanged?.();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const togglePerm = async (uid, val) => {
        try {
            await api.put(`/users/${uid}/letterhead-permission`, { can_use_letterhead: val });
            setUsers((p) => p.map((u) => u.id === uid ? { ...u, can_use_letterhead: val } : u));
            toast.success(lang === "de" ? "Berechtigung aktualisiert" : "Permission updated");
        } catch (e) { toast.error(errMsg(e)); }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="letterhead-manager">
                <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-brand-500" />
                        <p className="font-semibold">{lang === "de" ? "Firmenbriefpapier verwalten" : "Manage letterheads"}</p>
                    </div>
                    <button onClick={onClose} className="hover:bg-neutral-100 rounded p-1" data-testid="lh-close"><X className="w-4 h-4" /></button>
                </header>

                <div className="flex-1 overflow-y-auto">
                    {/* Upload zone */}
                    <div className="p-4 border-b border-neutral-200">
                        <p className="text-xs font-semibold text-neutral-700 mb-2">{lang === "de" ? "Neues Briefpapier hochladen" : "Upload new letterhead"}</p>
                        <input
                            type="text"
                            value={uploadName}
                            onChange={(e) => setUploadName(e.target.value)}
                            placeholder={lang === "de" ? "Name (optional)" : "Name (optional)"}
                            className="w-full h-8 text-sm border border-neutral-200 rounded px-2 mb-2"
                            data-testid="lh-upload-name"
                        />
                        <DropZone
                            accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf"
                            onFile={doUpload}
                            disabled={busy}
                            label={lang === "de" ? "PNG · JPG · PDF hierher ziehen, Str+V einfügen oder klicken" : "Drop PNG/JPG/PDF here, paste with Ctrl+V, or click"}
                            testId="lh-dropzone"
                        />
                        {busy && <div className="flex items-center gap-2 text-xs text-neutral-500 mt-2"><Loader2 className="w-3 h-3 animate-spin" /> {lang === "de" ? "Lade hoch…" : "Uploading…"}</div>}
                    </div>

                    {/* Templates */}
                    <div className="p-4 border-b border-neutral-200">
                        <p className="text-xs font-semibold text-neutral-700 mb-2">{lang === "de" ? "Vorhandene Vorlagen" : "Existing templates"}</p>
                        {items.length === 0 ? (
                            <p className="text-xs text-neutral-400">{lang === "de" ? "Noch keine Vorlage hochgeladen." : "No templates yet."}</p>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="lh-list">
                                {items.map((it) => (
                                    <div key={it.id} className="border border-neutral-200 rounded-md overflow-hidden flex flex-col" data-testid={`lh-item-${it.id}`}>
                                        <div className="aspect-[210/297] bg-neutral-50 border-b border-neutral-200 relative">
                                            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/letterheads/${it.id}/image`} alt={it.name} className="absolute inset-0 w-full h-full object-contain" />
                                        </div>
                                        <div className="p-2 text-xs flex items-center justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <p className="font-semibold truncate">{it.name}</p>
                                                <p className="text-[10px] text-neutral-500 truncate">{it.filename}</p>
                                                <p className="text-[10px] text-neutral-500">{(it.allowed_user_ids || []).length} {lang === "de" ? "Whitelist-User" : "whitelisted"}</p>
                                            </div>
                                            <button onClick={() => setEditing({ ...it, allowed_user_ids: it.allowed_user_ids || [] })} className="p-1 hover:bg-neutral-100 rounded" title={lang === "de" ? "Zugriff verwalten" : "Manage access"} data-testid={`lh-edit-${it.id}`}>
                                                <Users className="w-3.5 h-3.5 text-brand-500" />
                                            </button>
                                            <button onClick={() => delLetterhead(it.id)} className="p-1 hover:bg-red-50 rounded" data-testid={`lh-del-${it.id}`}>
                                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Global user permission flag */}
                    <div className="p-4">
                        <p className="text-xs font-semibold text-neutral-700 mb-2">{lang === "de" ? "Globale Briefpapier-Berechtigung pro User" : "Global letterhead permission per user"}</p>
                        <p className="text-[11px] text-neutral-500 mb-2">{lang === "de" ? "Diese User dürfen alle Briefpapier-Vorlagen verwenden. Admins/Master haben immer Zugriff." : "These users can use any letterhead. Admins/masters always can."}</p>
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                            {users.map((u) => (
                                <label key={u.id} className="flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-neutral-50 rounded cursor-pointer">
                                    <span className="text-sm">{u.name} <span className="text-[10px] text-neutral-400">({u.role})</span></span>
                                    <input
                                        type="checkbox"
                                        checked={!!u.can_use_letterhead || u.role === "admin" || u.role === "master"}
                                        disabled={u.role === "admin" || u.role === "master"}
                                        onChange={(e) => togglePerm(u.id, e.target.checked)}
                                        data-testid={`lh-perm-${u.id}`}
                                    />
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <footer className="px-4 py-3 border-t border-neutral-200 flex justify-end">
                    <Button variant="outline" onClick={onClose} data-testid="lh-close-btn">{lang === "de" ? "Schließen" : "Close"}</Button>
                </footer>
            </div>

            {/* Per-template whitelist editor */}
            {editing && (
                <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setEditing(null)}>
                    <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="lh-access-dialog">
                        <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                            <p className="font-semibold">{lang === "de" ? "Zugriff verwalten" : "Manage access"}</p>
                            <button onClick={() => setEditing(null)}><X className="w-4 h-4" /></button>
                        </header>
                        <div className="p-4 space-y-3">
                            <input
                                value={editing.name}
                                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                className="w-full h-8 text-sm border border-neutral-200 rounded px-2"
                                placeholder={lang === "de" ? "Name" : "Name"}
                                data-testid="lh-edit-name"
                            />
                            <p className="text-[11px] text-neutral-500">{lang === "de" ? "Nur diese User dürfen dieses Briefpapier verwenden (zusätzlich zu globaler Berechtigung):" : "Only these users can use this template (in addition to global flag):"}</p>
                            <div className="space-y-1 max-h-60 overflow-y-auto">
                                {users.map((u) => {
                                    const ok = (editing.allowed_user_ids || []).includes(u.id);
                                    return (
                                        <label key={u.id} className="flex items-center gap-2 px-2 py-1 hover:bg-neutral-50 rounded cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={ok}
                                                onChange={() => {
                                                    const list = new Set(editing.allowed_user_ids || []);
                                                    if (ok) list.delete(u.id); else list.add(u.id);
                                                    setEditing({ ...editing, allowed_user_ids: Array.from(list) });
                                                }}
                                                data-testid={`lh-wl-${u.id}`}
                                            />
                                            <span className="text-sm">{u.name}</span>
                                            {(u.can_use_letterhead || u.role === "admin" || u.role === "master") && (
                                                <CheckCircle2 className="w-3 h-3 text-green-600 ml-auto" title={lang === "de" ? "hat bereits global Zugriff" : "already has global access"} />
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                        <footer className="px-4 py-3 border-t border-neutral-200 flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setEditing(null)}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                            <Button onClick={saveEditing} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="lh-edit-save">{lang === "de" ? "Speichern" : "Save"}</Button>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
}
